/**
 * Badge de estado (design-system §7 · Badge): píldora 12 px/500 con **icono + texto siempre**
 * (§10: el estado nunca se comunica solo por color).
 */
import {
  Ban,
  Check,
  CircleMinus,
  Circle,
  Clock,
  History,
  MessageSquare,
  Pencil,
  TriangleAlert,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

import type { ReactNode } from 'react';

export type BadgeVariant =
  | 'activa'
  | 'pendiente'
  | 'inactiva'
  | 'suspendida'
  | 'online'
  | 'offline'
  | 'valida'
  | 'observada'
  | 'manual'
  | 'anulada'
  /** 005: marca reemplazada por una corrección (FR-024). */
  | 'reemplazada'
  /** 007: solicitud de regularización pedida por el admin (contracts/ui.md §2.2). */
  | 'pedida'
  | 'aprobada'
  | 'rechazada'
  | 'cancelada';

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONE_CLASSES: Record<Tone, string> = {
  success: 'bg-fm-success-soft text-fm-success-text',
  warning: 'bg-fm-warning-soft text-fm-warning-text',
  danger: 'bg-fm-danger-soft text-fm-danger-text',
  info: 'bg-fm-info-soft text-fm-info-text',
  neutral: 'bg-fm-bg-sunken text-fm-text-secondary',
};

interface VariantSpec {
  tone: Tone;
  icon: LucideIcon;
  /** Punto sólido (online) vs. punto vacío (offline). */
  filled?: boolean;
}

const VARIANTS: Record<BadgeVariant, VariantSpec> = {
  activa: { tone: 'success', icon: Check },
  pendiente: { tone: 'warning', icon: Clock },
  inactiva: { tone: 'neutral', icon: CircleMinus },
  suspendida: { tone: 'danger', icon: Ban },
  online: { tone: 'success', icon: Circle, filled: true },
  offline: { tone: 'danger', icon: Circle },
  valida: { tone: 'success', icon: Check },
  observada: { tone: 'warning', icon: TriangleAlert },
  manual: { tone: 'info', icon: Pencil },
  anulada: { tone: 'danger', icon: XCircle },
  reemplazada: { tone: 'neutral', icon: History },
  // 007 · regularizaciones (contracts/ui.md §2.2). `pendiente` se reutiliza tal cual.
  pedida: { tone: 'info', icon: MessageSquare },
  aprobada: { tone: 'success', icon: Check },
  rechazada: { tone: 'danger', icon: XCircle },
  cancelada: { tone: 'neutral', icon: CircleMinus },
};

export interface BadgeProps {
  variant: BadgeVariant;
  /** Texto alternativo; por defecto el de `common.badges.<variant>`. */
  children?: ReactNode;
  className?: string;
}

export function Badge({ variant, children, className }: BadgeProps) {
  const spec = VARIANTS[variant];
  const Icon = spec.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-fm-1 rounded-fm-pill px-fm-2 py-px text-fm-xs font-medium',
        TONE_CLASSES[spec.tone],
        className,
      )}
    >
      <Icon size={12} aria-hidden="true" className={spec.filled ? 'fill-current' : undefined} />
      {children ?? t(`common.badges.${variant}`)}
    </span>
  );
}
